import { format, isValid, parseISO } from "date-fns";

// The batch push's words (#1088), shared by Review & Publish's step 3 and Push
// Changes (#1089), which passes its own outcome copy over `PUBLISH_PUSH_COPY`.
// The donor's sources: `CommunitySpecAutoComplete.tsx`,
// `SelectedCommunitySpecs.tsx`, `PublishVersionNonLinearStepper.tsx`
// (`RebaseFailureView`), `usePollRebaseStatus.ts`. The parity enumeration is on
// #1088.

/** `MAX_RETRY_ATTEMPTS` (`PublishVersionNonLinearStepper.tsx:102`). */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * The Parent version note (Q12, a named scope addition): where a community
 * stands against the version being pushed — "Up to date", "on V6 · 2 versions
 * behind", or, for a base version with no code, its published date in the
 * viewer's zone ("on the 3/2/2026 version"), the ruled example verbatim, with
 * no count. A version with neither (none on the load) keeps only the count.
 */
export function parentVersionNote(
  parent: { versionCode: string | null; publishedAt: string | null },
  versionsBehind: number,
): string {
  if (versionsBehind === 0) return "Up to date";
  const behind = `${versionsBehind} ${versionsBehind === 1 ? "version" : "versions"} behind`;
  const code = parent.versionCode?.trim();
  if (code) return `on V${code} · ${behind}`;
  const published = parent.publishedAt === null ? null : parseISO(parent.publishedAt);
  if (published !== null && isValid(published)) {
    // The count added as on a coded row (#1146 D17 = b, over Q12's wording).
    return `on the ${format(published, "M/d/yyyy")} version · ${behind}`;
  }
  return behind;
}

interface NamedCommunity {
  id: number;
  name: string;
  communityId: string | null;
}

/**
 * A row's name, told apart from a same-named row (Q9.4): " ({community
 * number})" after a name that repeats in the list, or the donor's own fallback
 * " (Spec #{id})" (`usePollRebaseStatus.ts:195-208`) when that number is
 * missing or repeats within the name — so every row stays distinguishable.
 */
export function communityLabel(row: NamedCommunity, rows: readonly NamedCommunity[]): string {
  const sameName = rows.filter((other) => other.name === row.name);
  if (sameName.length < 2) return row.name;
  const number = row.communityId?.trim();
  const shared = sameName.filter((other) => other.communityId?.trim() === number).length > 1;
  return number && !shared ? `${row.name} (${number})` : `${row.name} (Spec #${row.id})`;
}

/** The failure view's message (`usePollRebaseStatus.ts:221-223`). */
export function updatedMessage(updated: number, total: number): string {
  return `${updated} of ${total} Community Specifications were updated. The following failed:`;
}

/** The line under it after a failed retry (`RebaseFailureView`, `:125-130`). */
export function retryLine(attempt: number): string {
  const left = Math.max(MAX_RETRY_ATTEMPTS - attempt, 0);
  const remaining = left > 0 ? ` ${left} ${left === 1 ? "attempt" : "attempts"} remaining.` : "";
  return `Retry attempt ${attempt} of ${MAX_RETRY_ATTEMPTS} failed.${remaining}`;
}

/** The start's refusal of a version that can't be pushed (#1145 D4 = b): the
 *  click — Review & Publish, Push Changes or Retry — shows its own sentence,
 *  "Coastal v1.8 can't be pushed because it isn't a published, unarchived
 *  Base Specification version." */
export const START_NOT_PUSHABLE_TAG = "invalid-input:spec-push-start-not-pushable";

/** A failed retry request's one sentence (Q10.1, Q11.4): the attempt, then the
 *  message the view showed before it — or, when the start refused a version
 *  that can't be pushed, that refusal's own sentence (#1145 D4 = b). */
export function retryRequestFailed(attempt: number, previous: string): string {
  return `Retry attempt ${attempt} failed. ${previous}`;
}

/** The failure view's Retry: "Retry Failed", then the attempts left. */
export function retryButtonLabel(retryCount: number): string {
  return retryCount === 0
    ? "Retry Failed"
    : `Retry Again (${MAX_RETRY_ATTEMPTS - retryCount} left)`;
}

/** `RebaseFailureView`'s exhausted copy (`:150-155`). */
export const RETRIES_EXHAUSTED =
  "We were unable to update these specifications after 3 attempts. Our team has been notified and will investigate.";

/** A screen's outcome copy: its three error endings, and its Sentry report. */
export interface PushOutcomeCopy {
  catastrophic: string;
  pollError: string;
  timeout: string;
  /** Sentry's `feature` tag on retry exhaustion. */
  sentryFeature: string;
  /** The exception's message on retry exhaustion. */
  sentryMessage: (attempts: number) => string;
  /** PostHog `push_changes`' `screen` (Q10.6). */
  screen: "publish" | "push";
}

/** Review & Publish's step 3 — the Q19 wording, "Rebase" become push. */
export const PUBLISH_PUSH_COPY: PushOutcomeCopy = {
  catastrophic: "The version was published but pushing changes to Community Specifications failed.",
  pollError: "Could not verify the push status. The version was published.",
  timeout: "The push is taking longer than expected. The version was published. Check back later.",
  sentryFeature: "batch_push_retry_exhausted",
  sentryMessage: (attempts) => `Batch push failed after ${attempts} retry attempts`,
  screen: "publish",
};

/** Push Changes (`PushToCommunitySpecsDialog.tsx:90-96,271-289`), verbatim:
 *  Q19's rewording reached only the messages that said "Rebase". */
export const PUSH_CHANGES_COPY: PushOutcomeCopy = {
  catastrophic: "Some Community Specifications could not be updated. Please try again.",
  pollError:
    "We could not confirm whether the changes were pushed. Refresh the page and check the Community Specifications.",
  timeout:
    "Pushing changes is taking longer than expected. It may still be running, check the Community Specifications shortly.",
  sentryFeature: "retroactive_push_retry_exhausted",
  sentryMessage: (attempts) => `Retroactive push failed after ${attempts} retry attempts`,
  screen: "push",
};

/** The button's label and the dialog's title (`PushToCommunitySpecsButton.tsx:84-92`). */
export const PUSH_CHANGES_TITLE = "Push Changes to Community Specs";

/** The dialog's body line (`PushToCommunitySpecsDialog.tsx:421-424`). */
export const PUSH_CHANGES_SUBTITLE =
  "Push the changes from the latest published version of this Base Specification to the Community Specs you select below.";

/** The push's success notification (`PushToCommunitySpecsButton.tsx:100-113`). */
export const PUSH_CHANGES_SUCCESS = "Community Specifications updated successfully.";

/** The linked read's failure (`PushToCommunitySpecsDialog.tsx:98-101`). */
export const PUSH_CHANGES_LOAD_FAILED =
  "Failed to load linked Community Specifications. Close and reopen this dialog to try again.";

/** A refused start (`PushToCommunitySpecsDialog.tsx:344-354`), the backticks literal. */
export const PUSH_CHANGES_START_FAILED =
  "Failed to push changes to Community Specifications. Click `Push Changes` to try again.";

/** A row already on the pushed version (`CommunitySpecAutoComplete.tsx:173-179`). */
export const UP_TO_DATE_LABEL = "Up to date";

/**
 * Why Push Changes is disabled on a version with no Effective date (Q11.1, a
 * named parity break: the source disables it silently). Agent-worded (#1089
 * Assumption 6), naming the field as the publish form labels it.
 */
export const NO_EFFECTIVE_DATE_TOOLTIP =
  "This version has no Effective Date, so its changes can't be pushed to Community Specifications.";

/**
 * The dialog's banner (`PushToCommunitySpecsDialog.tsx:56-85`) from the linked
 * rows and the ones already up to date: success when none is behind, info when
 * some are, and none with no rows or with every row behind (Q11.6). The info
 * banner's first sentence has no singular form in the source, kept as it is.
 */
export function pushChangesBanner(
  total: number,
  upToDate: number,
): { severity: "success" | "info"; text: string } | null {
  const stale = total - upToDate;
  if (total === 0 || upToDate === 0) return null;
  if (stale === 0) {
    return {
      severity: "success",
      text:
        total === 1
          ? "The linked Community Spec is already up to date with the latest published version. There is nothing to push."
          : `All ${total} linked Community Specs are already up to date with the latest published version. There is nothing to push.`,
    };
  }
  const others =
    upToDate === 1
      ? "The other one is already up to date and is marked below."
      : `The other ${upToDate} are already up to date and are marked below.`;
  return {
    severity: "info",
    text: `${stale} of ${total} Community Specs are behind the latest published version and can be updated. ${others}`,
  };
}

/** The linked read's row, as the picker and the selected list draw it. */
export interface LinkedCommunityFacts extends NamedCommunity {
  newestVersion: { isPublished: boolean; versionCode: string | null } | null;
  parentVersion: { versionCode: string | null; publishedAt: string | null };
  versionsBehind: number;
}

/**
 * One picker row from the linked read: the name with its suffix (Q9.4), the
 * draft's "(v{code})" and the chip off the newest version — the donor's
 * `versions[0]` (`CSA:126-128`), a community with no version reading Draft —
 * and the Parent version note (Q12).
 */
export function pickerRow(row: LinkedCommunityFacts, rows: readonly LinkedCommunityFacts[]) {
  const published = row.newestVersion?.isPublished ?? false;
  const code = row.newestVersion?.versionCode?.trim();
  return {
    id: row.id,
    name: row.name,
    label: communityLabel(row, rows),
    draftCode: !published && code ? code : null,
    published,
    note: parentVersionNote(row.parentVersion, row.versionsBehind),
  };
}

// --- the push preview (#1090; #1049's brief, rulings R1–R9; Q13–Q15) ---------------

/** Each row's "Preview changes" link, and its accessible name. */
export const PREVIEW_CHANGES_LINK = "Preview changes";
export const previewChangesLabel = (community: string) => `Preview changes for ${community}`;

/** The dialog's footer: back to the picker, and the read-only view's close. */
export const BACK_TO_COMMUNITY_SPECIFICATIONS = "Back to Community Specifications";
export const PREVIEW_CLOSE = "Close";

/** The preview's title; the read-only view's, worded in the past (Q15). */
export const previewTitle = (community: string) => `Preview changes: ${community}`;
export const historyTitle = (base: string) => `Changes from ${base}`;

/** The kinds, in the filter's order, with their ruled words. */
export const PUSH_KIND_LABEL = {
  added: "Added",
  changed: "Changed",
  moved: "Moved",
  removed: "Removed",
} as const;

/** The Draft tabs' names (ruled). */
export const PUBLISHED_TAB = "Published version";
export const DRAFT_TAB = "Draft";

/** A body with no change to show: the race step 3 and Push Changes rule out (Assumption 15). */
export const upToDateText = (community: string, base: string) =>
  `${community} is already up to date with ${base}.`;

/** A failed read (Assumption 14). */
export const PREVIEW_LOAD_FAILED = "Couldn't load the preview.";
export const PREVIEW_TRY_AGAIN = "Try again";

/** The not-applied section's fixed words (Q14, R9); `Reset` goes in bold. */
export const notAppliedExplanation = (community: string, base: string) => ({
  before: `${community} has its own version of these rows, so ${base}'s changes to them weren't applied. ${community} keeps what it has. To take the base's version of a line instead, use `,
  bold: "Reset",
  after: ` on that line in ${community}.`,
});
